import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, ApiError } from '../types';

// URL del backend. NON modificare manualmente per i rilasci:
//  - sviluppo (npm start): nessuna env → fallback http://localhost:8080
//  - produzione (docker compose build): REACT_APP_API_URL viene iniettato dal
//    build arg (root .env → DASHBOARD_API_URL) e sovrascrive il fallback.
// CRA: una env var già presente nell'ambiente vince sempre sul fallback inline.
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        console.log('🔐 API Request Interceptor:', {
          url: config.url,
          method: config.method?.toUpperCase(),
          hasToken: !!token,
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'No token'
        });
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('✅ Authorization header added to request');
        } else {
          console.warn('⚠️ No token found in localStorage for request to:', config.url);
        }
        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        console.log('✅ API Response Success:', {
          url: response.config.url,
          status: response.status,
          method: response.config.method?.toUpperCase()
        });
        return response;
      },
      (error) => {
        console.error('❌ API Response Error:', {
          url: error.config?.url,
          status: error.response?.status,
          method: error.config?.method?.toUpperCase(),
          message: error.response?.data?.message || error.message,
          data: error.response?.data
        });
        
        // Solo il 401 (token assente/scaduto/invalido) forza il logout.
        // Il 403 con RBAC è una risposta NORMALE per un admin parziale
        // (endpoint fuori dai suoi permessi): sloggarlo alla prima occorrenza
        // renderebbe la dashboard inutilizzabile per i collaboratori.
        if (error.response?.status === 401) {
          console.warn('🔒 401 - Token expired or invalid, forcing logout');
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          // Dispatch event so the React app can handle logout + redirect
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        } else if (error.response?.status === 403) {
          console.warn(`🔒 403 - Permesso mancante per ${error.config?.url} (nessun logout)`);
        }
        
        const apiError: ApiError = {
          message: error.response?.data?.message || error.message || 'An error occurred',
          code: error.response?.status?.toString() || 'UNKNOWN',
          details: error.response?.data
        };
        
        return Promise.reject(apiError);
      }
    );
  }

  // Generic GET request
  async get<T>(endpoint: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.api.get(endpoint, { params });
    return response.data;
  }

  // Generic POST request
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post(endpoint, data);
    return response.data;
  }

  // Generic PUT request
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put(endpoint, data);
    return response.data;
  }

  // Generic DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete(endpoint);
    return response.data;
  }

  // Generic PATCH request
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.patch(endpoint, data);
    return response.data;
  }

  // File upload
  async uploadFile<T>(endpoint: string, file: File, additionalData?: any): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Set auth token
  setAuthToken(token: string): void {
    localStorage.setItem('admin_token', token);
  }

  // Clear auth token
  clearAuthToken(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }

  // Get auth token
  getAuthToken(): string | null {
    return localStorage.getItem('admin_token');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    return !!token;
  }
}

export const apiService = new ApiService();
export default apiService;